import { CITY_UF_REGEX } from "../ticket/ticket-form.schema";

const COUNTRY_SUFFIX_RE = /,\s*(brasil|brazil)\s*$/i;
const STATE_CODE_RE = /^[A-Za-z]{2}$/;
const TOKEN_SEPARATOR_RE = /\s-\s|,\s*/;

/**
 * Splits a raw location string (as concatenated from a places-search result,
 * e.g. "Avenida Paulista - Bela Vista, São Paulo - SP, Brasil") into its
 * components, most specific first: [..., neighborhood, city, state].
 */
function tokenize(raw: string): string[] {
  return raw
    .replace(COUNTRY_SUFFIX_RE, "")
    .split(TOKEN_SEPARATOR_RE)
    .map((token) => token.trim())
    .filter(Boolean);
}

/**
 * Normalizes a free-text location search result into the "Cidade - UF"
 * format required by the ticket form. Returns null when the input can't be
 * confidently reduced to that format (caller should leave the field blank
 * rather than show a value that will fail validation anyway).
 */
export function formatCityUf(raw: string | undefined | null): string | null {
  const value = (raw ?? "").trim();
  if (!value) return null;

  const tokens = tokenize(value);
  if (tokens.length < 2) return null;

  const state = tokens[tokens.length - 1];
  const city = tokens[tokens.length - 2];
  if (!STATE_CODE_RE.test(state) || !city) return null;

  const result = `${city} - ${state.toUpperCase()}`;
  return CITY_UF_REGEX.test(result) ? result : null;
}

/**
 * Extracts a neighborhood from the same kind of composite location string,
 * when present (i.e. when the search was more specific than city level).
 */
export function extractNeighborhoodFromLocation(
  raw: string | undefined | null,
): string | null {
  const value = (raw ?? "").trim();
  if (!value) return null;

  const tokens = tokenize(value);
  if (tokens.length < 3) return null;

  const state = tokens[tokens.length - 1];
  if (!STATE_CODE_RE.test(state)) return null;

  const city = tokens[tokens.length - 2];
  const neighborhood = tokens[tokens.length - 3];
  if (!neighborhood) return null;
  // A city-level pick repeats the city in that slot ("São Paulo - São Paulo,
  // SP"), and an address-level one can leave a street number there.
  if (/^\d+$/.test(neighborhood)) return null;
  if (neighborhood.toLowerCase() === city.toLowerCase()) return null;

  return neighborhood;
}

/**
 * Builds the ticket's "Cidade - UF" from already-separated parts. Prefer this
 * over running formatCityUf on a street address: an address's city sits in no
 * fixed position, so "Rua A, 100 - Centro - SP" would confidently yield the
 * neighborhood — a value the ticket regex accepts and nobody catches.
 */
export function formatCityUfFromParts(
  city: string | undefined | null,
  state: string | undefined | null,
): string | null {
  const cityValue = (city ?? "").trim();
  if (!cityValue) return null;

  // The city field may already carry its state ("São Paulo - SP").
  const alreadyQualified = formatCityUf(cityValue);
  if (alreadyQualified) return alreadyQualified;

  const stateValue = (state ?? "").trim();
  if (!STATE_CODE_RE.test(stateValue)) return null;

  const result = `${cityValue} - ${stateValue.toUpperCase()}`;
  return CITY_UF_REGEX.test(result) ? result : null;
}

/**
 * Reads the state code off the end of a free-text address. Only the trailing
 * token is trusted — unlike the city, a final "… , SP" is unambiguous.
 */
export function extractStateFromAddress(
  raw: string | undefined | null,
): string | null {
  const tokens = tokenize((raw ?? "").trim());
  if (!tokens.length) return null;

  const state = tokens[tokens.length - 1];
  return STATE_CODE_RE.test(state) ? state.toUpperCase() : null;
}
